// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    extendedStyles?: XngQuantityBreakPriceStyles;
    showMoreLimit?: number;
    product: ProductContextModel | CartLineModel;
}

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    return {
        isAuthenticated: session && (session.isAuthenticated || session.rememberMe) && !session.isGuest,
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;
export interface XngQuantityBreakPriceStyles {
    qty?: TypographyPresentationProps;
    title?: TypographyPresentationProps;
    price?: TypographyPresentationProps;
    wraper?: InjectableCss;
    priceWraper?: InjectableCss;
    seeLessLink?: LinkPresentationProps;
    seeAllLink?: LinkPresentationProps;
    lineWraper?: InjectableCss;
}

const commonTextStyle: TypographyPresentationProps = {
    size: 12,
    lineHeight: "14px",
}

export const xngQuantityBreakPrice: XngQuantityBreakPriceStyles = {
    wraper: {
        css: css`
            border: 1px solid ${getColor("common.border")};
            padding: 4px 8px;
            min-width: 210px;
            height: min-content;
        `,
    },
    title: {
        ...commonTextStyle,
    },
    price: {
        ...commonTextStyle,
    },
    qty: {
        ...commonTextStyle,
        css: css`
            margin-right: 20px;
        `,
    },
    seeLessLink: {
        typographyProps: {
            ...commonTextStyle,
        },
        css: css`
            padding-top: 5px;
        `,
    },
    seeAllLink: {
        typographyProps: {
            ...commonTextStyle,
        },
        css: css`
            padding-top: 5px;
        `,
    },
    lineWraper: {
        css: css`
            display: flex;
            justify-content: space-between;
        `,
    },
};

const XngQuantityBreakPrice = ({ product: pricingData, extendedStyles, showMoreLimit, isAuthenticated }: Props) => {
    const [styles] = useState(() => mergeToNew(xngQuantityBreakPrice, extendedStyles));
    const [expanded, setExpanded] = useState(false);

    const productContextModel = pricingData as ProductContextModel;
    const cartLineModel = pricingData as CartLineModel;

    const breakPricingDataProduct = productContextModel.productInfo?.pricing?.additionalResults?.priceInfoList;
    const breakPricingDataCartLine = cartLineModel.properties?.priceInfoList;
    const productPrice = productContextModel.productInfo?.pricing?.unitNetPrice;

    const priceInfoListJson = breakPricingDataProduct || breakPricingDataCartLine;
    if (!priceInfoListJson || !isAuthenticated || productPrice === 0) {
        return null;
    }
    const priceInfoList = JSON.parse(priceInfoListJson);
    const limitedBreakPrices = expanded ? priceInfoList : priceInfoList?.slice(0, showMoreLimit || 3);

    return (
        <>
            {priceInfoList && (
                <StyledWrapper {...styles.wraper}>
                    <Typography {...styles.title}>{siteMessage("Hardwoods_QuantityBreakPrice")}</Typography>

                    {limitedBreakPrices.map((priceInfo: any, index: any) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <StyledWrapper {...styles.lineWraper} key={`${priceInfo.productNumber}_${index}`}>
                            {priceInfo.from === priceInfo.to ? (
                                <Typography {...styles.qty}>
                                    {priceInfo.from === 0 ? "1" : priceInfo.from}+ {priceInfo.priceUom}
                                </Typography>
                            ) : (
                                <Typography {...styles.qty}>
                                    {priceInfo.from === 0 ? "1" : priceInfo.from} - {priceInfo.to - 1} {priceInfo.priceUom}
                                </Typography>
                            )}
                            <Typography {...styles.price}>{priceInfo.priceUomNetPrice}</Typography>
                        </StyledWrapper>
                    ))}
                    {priceInfoList &&
                        priceInfoList.length > (showMoreLimit || 3) &&
                        (expanded ? (
                            <Link {...styles.seeLessLink} onClick={() => setExpanded(false)}>
                                {translate("View Less")}
                            </Link>
                        ) : (
                            <Link {...styles.seeAllLink} onClick={() => setExpanded(true)}>
                                {translate("View More")}
                            </Link>
                        ))}
                </StyledWrapper>
            )}
        </>
    );
};

export default connect(mapStateToProps)(XngQuantityBreakPrice);
